import { FC } from 'react';
import { Trash2 } from 'lucide-react';
import { cn } from '@new/lib';
import { getSlotStatus, isSlotBooked } from '../../lib/helpers';
import type { UserAvailabilitySlot } from '@teachur/http-client';

interface CalendarTimeSlotProps {
  dayIdx: number;
  hour: number;
  weekStart: Date;
  availabilitySlots: UserAvailabilitySlot[];
  slotClassName: string;
  onMouseDown: (dayIdx: number, hour: number) => void;
  onMouseEnter: (dayIdx: number, hour: number) => void;
  onSlotClick: (slot: UserAvailabilitySlot) => void;
}

export const CalendarTimeSlot: FC<CalendarTimeSlotProps> = ({
  dayIdx,
  hour,
  weekStart,
  availabilitySlots,
  slotClassName,
  onMouseDown,
  onMouseEnter,
  onSlotClick,
}) => {
  const existingSlot = isSlotBooked(
    { day: dayIdx, hour, date: new Date() },
    weekStart,
    availabilitySlots
  );
  const status = getSlotStatus(existingSlot);

  const renderSlotContent = () => {
    if (status === 'accepted') {
      return <div className="text-xs font-medium text-(--secondary-B800)">Booked</div>;
    }

    if (status === 'pending') {
      return <div className="text-xs font-medium text-(--warning-W700)">Pending</div>;
    }

    if (status === 'rejected') {
      return <div className="text-xs font-medium text-(--danger-D700)">Rejected</div>;
    }

    return (
      <div className="opacity-0 group-hover:opacity-100 transition-opacity">
        <Trash2 className="w-4 h-4 text-(--danger-D700)" />
      </div>
    );
  };

  return (
    <div
      key={`${dayIdx}-${hour}`}
      className={cn('bg-(--neutral-white-NW1000) min-h-9 sm:min-h-15 p-1 relative', slotClassName)}
      onMouseDown={() => onMouseDown(dayIdx, hour)}
      onMouseEnter={() => onMouseEnter(dayIdx, hour)}
      onClick={() => existingSlot && onSlotClick(existingSlot)}
    >
      {existingSlot && (
        <div className="h-full flex flex-col items-center justify-center">
          {renderSlotContent()}
        </div>
      )}
    </div>
  );
};
